import { SideOverDescription, SideOverHeading, ElementsButton, Icon, SideOverHeader } from '../../components';
import { MonetaryAmount } from '../../../generated/graphql';
import { displayMonetaryAmount, Account, Asset } from '../../../util';
import { displayAccountType } from '../../util';
import { getTotalOwnershipPercentage } from '../../../Owner/util';
import { fadeSlideIn } from '../../animation';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

type CompleteMergePageProps = {
  targetAccount: Account;
  sourceAccount: Account;
  exitRoute?: string;
  onSubmit?: () => void;
};

export const CompleteMergePage = ({ targetAccount, sourceAccount, exitRoute, onSubmit }: CompleteMergePageProps) => {
  const { t: tLoan } = useTranslation('loan');
  const { t: tCommon } = useTranslation('common');
  const { t: tCashAccount } = useTranslation('cashAccount');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tRealEstate } = useTranslation('realEstate');

  return (
    <>
      <SideOverHeader exitText={tCommon('back')} exit={exitRoute} exitIcon={<Icon icon="back" />} />
      <SideOverHeading className="mb-8" heading={tCommon('complete-account-merge-heading')} />
      <SideOverDescription description={tCommon('complete-account-merge-description')} />
      {targetAccount && sourceAccount && (
        <motion.div {...fadeSlideIn()} className="grid w-full gap-4">
          <div className="flex h-auto min-h-[97px] items-center justify-between rounded-2xl bg-darkMode-gray-medium/[0.16] px-6">
            <div>
              <p className="text-2xl font-bold leading-5 text-white">{targetAccount?.name}</p>
              <p className="text-lg leading-[21px]">
                {displayAccountType(targetAccount, tLoan, tCashAccount, tCommon, tInvestment, tRealEstate)}
              </p>
            </div>
            <p className="text-2xl font-bold leading-[21px] text-white">
              {displayMonetaryAmount(
                targetAccount.__typename === 'Loan'
                  ? (targetAccount.balance.latestDataPoint?.data.value as MonetaryAmount)
                  : ((targetAccount as Asset).value.latestDataPoint?.data.value as MonetaryAmount),
                {
                  ownershipPercentage: getTotalOwnershipPercentage(targetAccount.ownership),
                }
              )}
            </p>
          </div>
          <div className="flex items-start justify-start">
            <Icon icon="mergeDirection" className="mx-3 mt-5 h-8 w-8 text-darkMode-gray-medium" />
            <div className="relative flex h-auto min-h-[97px] flex-1 items-center justify-between rounded-2xl bg-darkMode-gray-medium/[0.16] px-6">
              <div>
                <p className="text-2xl font-bold leading-5 text-white line-through">{sourceAccount?.name}</p>
                <p className="text-lg leading-[21px] line-through">
                  {displayAccountType(sourceAccount, tLoan, tCashAccount, tCommon, tInvestment, tRealEstate)}
                </p>
              </div>
              <p className="text-2xl font-bold leading-[21px] text-white line-through">
                {displayMonetaryAmount(
                  sourceAccount.__typename === 'Loan'
                    ? (sourceAccount.balance.latestDataPoint?.data.value as MonetaryAmount)
                    : ((sourceAccount as Asset).value.latestDataPoint?.data.value as MonetaryAmount),
                  {
                    ownershipPercentage: getTotalOwnershipPercentage(sourceAccount.ownership),
                  }
                )}
              </p>
            </div>
          </div>
          <ElementsButton
            className="mt-20 w-fit"
            size="large"
            icon="check"
            onClick={onSubmit}
            label={tCommon('complete-account-merge-button')}
          />
        </motion.div>
      )}
    </>
  );
};
